import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import DashLayout from "@/components/DashLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import FileUploader from "@/components/FileUploader";
import { Upload, Download, Info } from "lucide-react";
import { toast } from "sonner";

const empty = {
  registration_no: "", first_name: "", middle_name: "", last_name: "",
  gender: "", mother_tongue: "", religion: "", blood_group: "", student_id: "",
  dob: "", admin_date: "", birth_place: "", age_on_last_april: "", preferred_mob: "", preferred_lang: "",
  class_name: "", section: "", fee_category: "", admin_no: "", boarding: "",
  board: "", caste_type: "", caste: "", birth_country: "India", nationality: "Indian",
  transport: false, route_id: "", photo_url: "",
  parent_details: { father: { name: "", qualification: "", annual_income: "", occupation_type: "", occupation: "" },
                    mother: { name: "", qualification: "", annual_income: "", occupation_type: "", occupation: "" },
                    guardian: { name: "", qualification: "", occupation_type: "", occupation: "" } },
  sibling_details: [],
  address_details: { permanent: "", correspondence: "", city: "", state: "", pincode: "" },
  medical_history: { allergies: "", conditions: "", medications: "" },
  sports_details: { interests: "", achievements: "" },
  other_info: { notes: "" },
  aadhaar_bank: { aadhaar: "", bank_name: "", account_no: "", ifsc: "" },
};

const FIELDS_1 = [
  ["registration_no", "Registration No"], ["first_name", "First Name *", true],
  ["middle_name", "Middle Name"], ["last_name", "Last Name"],
  ["student_id", "Student Id"], ["admin_no", "Admin No"],
  ["dob", "Date of Birth *", true, "date"], ["admin_date", "Admin Date *", true, "date"],
  ["birth_place", "Birth Place"], ["age_on_last_april", "Age on last April"],
  ["preferred_mob", "Preferred Mob"], ["preferred_lang", "Preferred Lang"],
];

const SELECT_FIELDS = {
  gender: ["Male", "Female", "Other"],
  mother_tongue: ["Hindi", "English", "Punjabi", "Tamil", "Telugu", "Marathi", "Bengali", "Gujarati", "Other"],
  religion: ["Hindu", "Muslim", "Christian", "Sikh", "Jain", "Buddhist", "Other"],
  blood_group: ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"],
  class_name: ["Nursery","LKG","UKG","1","2","3","4","5","6","7","8","9","10","11","12"],
  fee_category: ["General", "SC", "ST", "OBC", "EWS"],
  boarding: ["Day Scholar", "Hostel"],
  board: ["CBSE", "ICSE", "State", "IB"],
  caste_type: ["General", "OBC", "SC", "ST"],
  birth_country: ["India", "Nepal", "Bhutan", "Other"],
  nationality: ["Indian", "NRI", "Other"],
};

const OCC_TYPE = ["Government", "Business", "Private", "Retire", "Other"];

export default function Admissions() {
  const { id } = useParams();
  const nav = useNavigate();
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [routes, setRoutes] = useState([]);

  useEffect(() => {
    api.get("/erp/routes").then((r) => setRoutes(r.data)).catch(() => {});
    // Auto-generate a registration number for NEW admissions only.
    if (!id) {
      const now = new Date();
      const yy = String(now.getFullYear()).slice(-2);
      const rand = Math.floor(1000 + Math.random() * 9000);
      setF((prev) => ({ ...prev, registration_no: `REG-${yy}${String(now.getMonth() + 1).padStart(2, "0")}-${rand}` }));
    }
  }, [id]);

  useEffect(() => {
    if (id) api.get(`/students/${id}`).then((r) => setF({ ...empty, ...r.data,
      parent_details: { ...empty.parent_details, ...(r.data.parent_details || {}) },
      address_details: { ...empty.address_details, ...(r.data.address_details || {}) },
      medical_history: { ...empty.medical_history, ...(r.data.medical_history || {}) },
      sports_details: { ...empty.sports_details, ...(r.data.sports_details || {}) },
      other_info: { ...empty.other_info, ...(r.data.other_info || {}) },
      aadhaar_bank: { ...empty.aadhaar_bank, ...(r.data.aadhaar_bank || {}) },
    }));
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (id) await api.put(`/students/${id}`, f);
      else await api.post("/students", f);
      toast.success(id ? "Updated" : "Admission created");
      nav("/school/students");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const set = (k, v) => setF({ ...f, [k]: v });
  const setNested = (section, k, v) => setF({ ...f, [section]: { ...f[section], [k]: v } });
  const setDeep = (section, sub, k, v) => setF({ ...f, [section]: { ...f[section], [sub]: { ...f[section][sub], [k]: v } } });

  return (
    <DashLayout variant="school" title={id ? "Edit Admission" : "New Admission"}>
      {!id && (
        <Card className="p-4 mb-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200" data-testid="admission-bulk-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
              <Upload className="w-5 h-5 text-blue-700" />
            </div>
            <div className="flex-1">
              <div className="font-bold text-sm text-blue-900">Bulk Import / Export Admissions</div>
              <div className="text-xs text-slate-600 mt-1">
                Upload a CSV with all student details in one go — registration numbers are auto-generated for blank rows.
                After import, just upload each student's photo & documents from the Students register.
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <label className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold border border-blue-300 bg-white rounded-md cursor-pointer hover:bg-blue-50" data-testid="admissions-import-csv">
                  <Upload className="w-3.5 h-3.5" /> Import CSV
                  <input type="file" accept=".csv" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0]; if (!file) return;
                    const fd = new FormData(); fd.append("file", file);
                    try {
                      const { data } = await api.post("/students-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
                      toast.success(`Imported ${data.imported} students${data.errors?.length ? ` (${data.errors.length} errors)` : ""}`);
                      if (data.errors?.length) console.warn("Import errors:", data.errors);
                      nav("/school/students");
                    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Import failed"); }
                    e.target.value = "";
                  }} />
                </label>
                <Button type="button" size="sm" variant="outline" onClick={async () => {
                  try {
                    const resp = await api.get("/students-export", { responseType: "blob" });
                    const url = URL.createObjectURL(resp.data);
                    const a = document.createElement("a"); a.href = url; a.download = "students.csv"; a.click();
                  } catch (e) { toast.error("Export failed"); }
                }} data-testid="admissions-export-csv"><Download className="w-3.5 h-3.5 mr-1" /> Export CSV</Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => {
                  const cols = ["registration_no","first_name","middle_name","last_name","gender","dob","admin_date","class_name","section","fee_category","board","blood_group","religion","preferred_mob","birth_country","nationality"];
                  const blob = new Blob([cols.join(",") + "\n"], { type: "text/csv" });
                  const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "students-template.csv"; a.click();
                }} data-testid="admissions-sample-csv"><Info className="w-3.5 h-3.5 mr-1" /> Sample Template</Button>
              </div>
            </div>
          </div>
        </Card>
      )}
      <form onSubmit={submit} className="space-y-6" data-testid="admission-form">
        <Card className="p-6">
          <h3 className="font-bold mb-4">Basic Details</h3>
          <div className="mb-6 flex items-center gap-4">
            <div className="w-28 h-32 bg-slate-100 border-2 border-dashed border-slate-300 rounded flex items-center justify-center overflow-hidden">
              {f.photo_url ? <img src={f.photo_url} alt="student" className="w-full h-full object-cover" /> : <span className="text-xs text-slate-400 text-center">Passport<br/>Photo</span>}
            </div>
            <div>
              <Label className="mb-1 block text-xs">Passport Photograph</Label>
              <FileUploader folder="students/photos" label="Upload photo" testId="upload-student-photo" onUploaded={(url) => set("photo_url", url)} />
              {f.photo_url && <Button type="button" variant="ghost" size="sm" className="ml-2 text-red-600" onClick={() => set("photo_url", "")}>Remove</Button>}
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {FIELDS_1.map(([k, l, req, t]) => (
              <div key={k}><Label className="mb-1 block text-xs">{l}{k === "registration_no" && <span className="text-slate-400 text-[10px] ml-1">(auto-generated · locked)</span>}</Label>
                <Input required={req} readOnly={k === "registration_no"} type={t || "text"} value={f[k]} onChange={(e) => set(k, e.target.value)} className={k === "registration_no" ? "bg-slate-50 cursor-not-allowed font-mono" : ""} data-testid={`af-${k}`} /></div>
            ))}
            {Object.entries(SELECT_FIELDS).map(([k, opts]) => (
              <div key={k}><Label className="mb-1 block text-xs capitalize">{k.replace(/_/g, " ")}</Label>
                <Select value={f[k]} onValueChange={(v) => set(k, v)}>
                  <SelectTrigger data-testid={`af-${k}`}><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{opts.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select></div>
            ))}
            <div><Label className="mb-1 block text-xs">Caste</Label><Input value={f.caste} onChange={(e) => set("caste", e.target.value)} data-testid="af-caste" /></div>
            <div><Label className="mb-1 block text-xs">Section</Label><Input value={f.section} onChange={(e) => set("section", e.target.value)} data-testid="af-section" /></div>
            <div><Label className="mb-1 block text-xs">Transport?</Label>
              <RadioGroup value={f.transport ? "yes" : "no"} onValueChange={(v) => set("transport", v === "yes")} className="flex gap-4 mt-2">
                <label className="flex items-center gap-2"><RadioGroupItem value="yes" data-testid="af-transport-yes" /> Yes</label>
                <label className="flex items-center gap-2"><RadioGroupItem value="no" data-testid="af-transport-no" /> No</label>
              </RadioGroup></div>
            {f.transport && (
              <div><Label className="mb-1 block text-xs">Bus Route *</Label>
                <Select value={f.route_id || ""} onValueChange={(v) => set("route_id", v)}>
                  <SelectTrigger data-testid="af-route"><SelectValue placeholder="Select route" /></SelectTrigger>
                  <SelectContent>
                    {routes.length === 0 && <SelectItem value="__none" disabled>No routes yet — create in Transport</SelectItem>}
                    {routes.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </Card>

        <Tabs defaultValue="parent">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="parent" data-testid="tab-parent">Parent Details</TabsTrigger>
            <TabsTrigger value="address" data-testid="tab-address">Address</TabsTrigger>
            <TabsTrigger value="medical" data-testid="tab-medical">Medical</TabsTrigger>
            <TabsTrigger value="sports" data-testid="tab-sports">Sports</TabsTrigger>
            <TabsTrigger value="other" data-testid="tab-other">Other</TabsTrigger>
            <TabsTrigger value="aadhaar" data-testid="tab-aadhaar">Aadhaar & Bank</TabsTrigger>
          </TabsList>
          <TabsContent value="parent"><Card className="p-6 space-y-6">
            {["father", "mother", "guardian"].map((role) => (
              <div key={role}>
                <h4 className="font-semibold capitalize mb-3">{role === "guardian" ? "Local Guardian" : role}</h4>
                <div className="grid md:grid-cols-3 gap-3">
                  <Input placeholder="Name" value={f.parent_details[role].name} onChange={(e) => setDeep("parent_details", role, "name", e.target.value)} data-testid={`af-${role}-name`} />
                  <Input placeholder="Qualification" value={f.parent_details[role].qualification} onChange={(e) => setDeep("parent_details", role, "qualification", e.target.value)} />
                  {role !== "guardian" && <Input placeholder="Annual Income" value={f.parent_details[role].annual_income} onChange={(e) => setDeep("parent_details", role, "annual_income", e.target.value)} />}
                  <Select value={f.parent_details[role].occupation_type} onValueChange={(v) => setDeep("parent_details", role, "occupation_type", v)}>
                    <SelectTrigger><SelectValue placeholder="Occupation Type" /></SelectTrigger>
                    <SelectContent>{OCC_TYPE.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}{role === "mother" && <SelectItem value="Housewife">Housewife</SelectItem>}</SelectContent>
                  </Select>
                  <Input placeholder="Occupation" value={f.parent_details[role].occupation} onChange={(e) => setDeep("parent_details", role, "occupation", e.target.value)} />
                </div>
              </div>
            ))}
          </Card></TabsContent>
          <TabsContent value="address"><Card className="p-6 grid md:grid-cols-2 gap-4">
            <Textarea placeholder="Permanent Address" value={f.address_details.permanent} onChange={(e) => setNested("address_details", "permanent", e.target.value)} data-testid="af-addr-permanent" />
            <Textarea placeholder="Correspondence Address" value={f.address_details.correspondence} onChange={(e) => setNested("address_details", "correspondence", e.target.value)} />
            <Input placeholder="City" value={f.address_details.city} onChange={(e) => setNested("address_details", "city", e.target.value)} />
            <Input placeholder="State" value={f.address_details.state} onChange={(e) => setNested("address_details", "state", e.target.value)} />
            <Input placeholder="Pincode" value={f.address_details.pincode} onChange={(e) => setNested("address_details", "pincode", e.target.value)} />
          </Card></TabsContent>
          <TabsContent value="medical"><Card className="p-6 space-y-3">
            <Input placeholder="Allergies" value={f.medical_history.allergies} onChange={(e) => setNested("medical_history", "allergies", e.target.value)} />
            <Input placeholder="Conditions" value={f.medical_history.conditions} onChange={(e) => setNested("medical_history", "conditions", e.target.value)} />
            <Input placeholder="Medications" value={f.medical_history.medications} onChange={(e) => setNested("medical_history", "medications", e.target.value)} />
          </Card></TabsContent>
          <TabsContent value="sports"><Card className="p-6 space-y-3">
            <Input placeholder="Interests" value={f.sports_details.interests} onChange={(e) => setNested("sports_details", "interests", e.target.value)} />
            <Input placeholder="Achievements" value={f.sports_details.achievements} onChange={(e) => setNested("sports_details", "achievements", e.target.value)} />
          </Card></TabsContent>
          <TabsContent value="other"><Card className="p-6"><Textarea rows={5} placeholder="Notes" value={f.other_info.notes} onChange={(e) => setNested("other_info", "notes", e.target.value)} /></Card></TabsContent>
          <TabsContent value="aadhaar"><Card className="p-6 grid md:grid-cols-2 gap-3">
            <Input placeholder="Aadhaar Number" value={f.aadhaar_bank.aadhaar} onChange={(e) => setNested("aadhaar_bank", "aadhaar", e.target.value)} data-testid="af-aadhaar" />
            <Input placeholder="Bank Name" value={f.aadhaar_bank.bank_name} onChange={(e) => setNested("aadhaar_bank", "bank_name", e.target.value)} />
            <Input placeholder="Account No" value={f.aadhaar_bank.account_no} onChange={(e) => setNested("aadhaar_bank", "account_no", e.target.value)} />
            <Input placeholder="IFSC" value={f.aadhaar_bank.ifsc} onChange={(e) => setNested("aadhaar_bank", "ifsc", e.target.value)} />
          </Card></TabsContent>
        </Tabs>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => nav("/school/students")}>Cancel</Button>
          <Button type="submit" disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="admission-submit">{id ? "Update Admission" : "Save Admission"}</Button>
        </div>
      </form>
    </DashLayout>
  );
}
